import * as React from "react";
import { useContext } from "react";
import { Search, SearchProps } from "./components/Search";
import { useQueryParams } from "../../hooks/useQueryParams";
import { SearchContext } from "../SearchContext";
import { Cell } from "./components/Cell";
import { BoardingType } from "../../types/BoardingType";
import { Gender } from "../../types/Gender";
import { Age } from "../../types/Age";
import { Field } from "./components/Field";
import { Picker } from "./components/Picker";
import { Radio } from "./components/Radio";
import { Select } from "./components/Select";
import { Speciality } from "../../types/Speciality";

interface Data {
    q: string;
    boarding_type: BoardingType;
    gender: Gender;
    location: string;
    radius: number;
    ages: Age[];
    regions: string[];
    speciality: Speciality[];
}

export const SpecialSearch: React.FC<SearchProps> = ({
    container,
    type,
    couldNotResolveLocation
}) => {
    const {
        qsOverride,
        regions,
        ages,
        gender,
        boarding_type,
        speciality
    } = useContext(SearchContext);
    const params = useQueryParams(qsOverride);

    const fields: Data = {
        q: params.getQuery(),
        boarding_type: params.getBoardingType(),
        gender: params.getGender(),
        location: params.getLocation(),
        radius: params.getRadius(),
        ages: params.getAges(),
        regions: params.getRegions(),
        speciality: params.getSpeciality()
    };

    return (
        <Search container={container} type={type} data={fields}>
            <Cell width="50">
                <Field<Data, "q"> name="q">
                    {(value, setValue) => (
                        <input
                            className="field"
                            type="text"
                            placeholder="Search by school name or location..."
                            value={value}
                            onChange={e => setValue(e.target.value)}
                        />
                    )}
                </Field>
            </Cell>

            <Cell width="50">
                <Field<Data, "speciality"> name="speciality">
                    {(value, setValue) => (
                        <Picker
                            label="Speciality"
                            selected={value}
                            onChange={setValue}
                            options={speciality}
                        />
                    )}
                </Field>
            </Cell>

            <Cell width="50">
                <Field<Data, "regions"> name="regions">
                    {(value, setValue) => (
                        <Picker
                            label="Region"
                            all={{ value: "all", label: "All" }}
                            selected={value}
                            onChange={setValue}
                            options={regions}
                        />
                    )}
                </Field>
            </Cell>

            <Cell width="50">
                <Field<Data, "ages"> name="ages">
                    {(value, setValue) => (
                        <Picker
                            label="Ages"
                            selected={value}
                            onChange={setValue}
                            options={ages}
                        />
                    )}
                </Field>
            </Cell>

            <Cell width="50">
                <Field<Data, "boarding_type"> name="boarding_type">
                    {(value, setValue) => (
                        <Radio
                            value={value}
                            onChange={setValue}
                            options={boarding_type}
                        />
                    )}
                </Field>
            </Cell>

            <Cell width="50">
                <Field<Data, "gender"> name="gender">
                    {(value, setValue) => (
                        <Radio
                            value={value}
                            onChange={setValue}
                            options={gender}
                        />
                    )}
                </Field>
            </Cell>

            <Cell width="80">
                <Field<Data, "location"> name="location">
                    {(value, setValue) => (
                        <input
                            className="field"
                            type="text"
                            placeholder="Enter a postcode to search around, and specify a range."
                            value={value}
                            onChange={e => setValue(e.target.value)}
                        />
                    )}
                </Field>

                {couldNotResolveLocation ? (
                    <p className="field-error">
                        We couldn't determine where this location is.
                    </p>
                ) : null}
            </Cell>

            <Cell width="20">
                <Field<Data, "radius"> name="radius">
                    {(value, setValue) => (
                        <Select
                            value={value}
                            onChange={setValue}
                            options={{
                                2: "~2mi",
                                5: "~5mi",
                                10: "~10mi",
                                15: "~15mi",
                                25: "~25mi",
                                50: "~50mi",
                                100: "~100mi"
                            }}
                        />
                    )}
                </Field>
            </Cell>
        </Search>
    );
};
